import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { translateSignal, TranslocoDirective } from '@jsverse/transloco';
import {
    LucideDynamicIcon,
    LucideEarthLock,
    LucideLogOut,
    LucideMessageCircleQuestionMark,
    LucideMonitor,
    LucideMoon,
    LucidePhone,
    LucideSun,
} from '@lucide/angular';
import { ButtonDirective } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SelectButton } from 'primeng/selectbutton';
import { Splitter } from 'primeng/splitter';

import { AdvaniaThemeService } from '@advania-cosoftware/advania-styles';
import {
    AuthService,
    CustomerContextService,
    IWolfEnvironment,
    LanguageService,
    WolfConfig,
} from '@advania-cosoftware/wolf-core';

import { GdprInfoComponent } from '../gdpr-info/gdpr-info.component';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SelectButton, FormsModule, Splitter, ButtonDirective, TranslocoDirective, LucideDynamicIcon],
})
export class UserProfileComponent {
    protected readonly ServiceNowIcon = LucideMessageCircleQuestionMark;
    protected readonly ContactUsIcon = LucidePhone;
    protected readonly GdprIcon = LucideEarthLock;
    protected readonly LogOutIcon = LucideLogOut;
    protected readonly english = translateSignal('English');
    protected readonly norwegian = translateSignal('Norwegian');

    private readonly _themeService = inject(AdvaniaThemeService);
    private readonly _auth = inject(AuthService);
    private readonly _customerContext = inject(CustomerContextService);
    private readonly _dialogService = inject(DialogService);
    private readonly _languageService = inject(LanguageService);
    private readonly _environment = inject<IWolfEnvironment>(WolfConfig);

    gdprDialog: DynamicDialogRef<GdprInfoComponent> | null = null;

    currentTheme = this._themeService.theme;
    availableThemes = this._themeService.themes.map((theme) => {
        switch (theme) {
            case 'light':
                return { theme: theme, icon: LucideSun };
            case 'dark':
                return { theme: theme, icon: LucideMoon };
            default:
                return { theme: theme, icon: LucideMonitor };
        }
    });

    languages = computed(() => [
        { value: 'en', text: this.english() },
        { value: 'no', text: this.norwegian() },
    ]);
    language = this._languageService.language;

    hasServiceNow = computed(() => {
        return this._customerContext.isFeatureEnabled('serviceNow');
    });

    version = this._environment.buildVersion;
    signOut(): void {
        this._auth.signOut();
    }

    openServiceNow() {
        window.open('https://portal.advania.no/support', '_blank');
    }

    openContactUs() {
        window.open('https://www.advania.no/om-oss/kontakt-oss', '_blank');
    }

    toggleGdpr() {
        this.gdprDialog = this._dialogService.open(GdprInfoComponent, {
            header: 'GDPR Information',
            width: '50vw',
            modal: true,
            closable: true,
            dismissableMask: true,
            breakpoints: {
                '960px': '75vw',
                '640px': '95vw',
            },
        });
    }
}
